import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { RECHNUNG_ART_LABELS, type RechnungArt } from "@/lib/dokumente";
import { getHeroDocuments, type HeroDocument } from "@/lib/projectOverview";
import { translateHeroDocumentType } from "@/projectOverview/heroDocumentDisplay";
import { useRealtime } from "@/lib/useRealtime";
import {
  BillingReopenDialog,
  type BillingReopenSubject,
} from "@/pages/dokumente/BillingReopenDialog";
import { ProjectAppDocumentsSection } from "@/pages/projekte/ProjectAppDocumentsSection";

function fmtDateTime(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("de-DE");
}

function fmtMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);
  } catch {
    return String(value);
  }
}

function fmtBool(value: boolean | null | undefined): string {
  if (value == null) return "—";
  return value ? "Ja" : "Nein";
}

function documentTitle(doc: HeroDocument): string {
  return String(doc.name || doc.title || doc.nr || "Dokument").trim();
}

function documentHref(doc: HeroDocument): string | null {
  const href = doc.url || doc.download_url;
  return href ? String(href) : null;
}

function documentSortKey(doc: HeroDocument): number {
  const raw = doc.created_at || doc.booking_modified;
  if (!raw) return 0;
  const t = new Date(String(raw)).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function documentTypeLabel(doc: HeroDocument): string {
  return translateHeroDocumentType(doc.type || doc.document_type_name);
}

function isHeroInvoice(doc: HeroDocument): boolean {
  if (doc.is_draft) return false;
  // Effektive Art aus dem Backend – invoice_style allein macht kein Angebot zur Rechnung.
  const art = String(doc.billing_art_override || doc.effective_rechnung_art || "")
    .trim()
    .toLowerCase();
  if (art === "rechnung" || art === "teilrechnung" || art === "vorkasse" || art === "schlussrechnung") {
    return true;
  }
  const typeBlob = [doc.type, doc.document_type_name, doc.document_base_type]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
  return typeBlob.includes("rechnung") || typeBlob.includes("invoice");
}

function heroRechnungArtLabel(doc: HeroDocument): string {
  const art = String(doc.billing_art_override || doc.effective_rechnung_art || "")
    .trim()
    .toLowerCase();
  if (!art) return "—";
  if (art === "rechnung") return "Rechnung";
  return RECHNUNG_ART_LABELS[art as RechnungArt] || art;
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="eh-project-doc-meta-row">
      <dt className="eh-project-doc-meta-row__label">{label}</dt>
      <dd className="eh-project-doc-meta-row__value">{value || "—"}</dd>
    </div>
  );
}

function DocumentCard({
  doc,
  canBillingReopen,
  onBillingReopen,
}: {
  doc: HeroDocument;
  canBillingReopen?: boolean;
  onBillingReopen?: () => void;
}) {
  const title = documentTitle(doc);
  const href = documentHref(doc);
  const isDraft = doc.is_draft === true;
  const artLabel = heroRechnungArtLabel(doc);

  return (
    <article className="eh-project-doc-card rounded-ios-lg border border-separator bg-card">
      <header className="eh-project-doc-card__header">
        <div className="min-w-0 flex-1">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-semibold text-accent hover:underline"
            >
              {title}
            </a>
          ) : (
            <h3 className="text-base font-semibold text-label">{title}</h3>
          )}
          <p className="mt-1 text-sm text-label-secondary">
            {[doc.nr, documentTypeLabel(doc)].filter((part) => part && part !== "—").join(" · ") ||
              "—"}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-start justify-end gap-2">
          {isDraft ? (
            <span className="rounded-full bg-status-orange/15 px-2.5 py-0.5 text-xs font-semibold text-status-orange">
              Entwurf
            </span>
          ) : null}
          {artLabel !== "—" ? (
            <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-semibold text-accent">
              {artLabel}
              {doc.billing_art_override ? " (App)" : ""}
            </span>
          ) : null}
          {doc.status_name ? (
            <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-semibold text-label-secondary">
              {doc.status_name}
            </span>
          ) : null}
        </div>
      </header>

      <dl className="eh-project-doc-card__meta">
        <MetaRow label="Erstellt" value={fmtDateTime(doc.created_at)} />
        <MetaRow label="Betrag" value={fmtMoney(doc.value ?? null)} />
        <MetaRow label="Dokumenttyp" value={documentTypeLabel(doc)} />
        <MetaRow label="Rechnungsart" value={artLabel} />
        <MetaRow label="Typ (Basis)" value={translateHeroDocumentType(doc.document_base_type)} />
        <MetaRow label="Status Dokument" value={String(doc.status_name || "—")} />
        <MetaRow label="Status-Code" value={String(doc.status_code ?? "—")} />
        <MetaRow label="Status Buchung" value={String(doc.booking_status_name || "—")} />
        <MetaRow label="Buchung geändert" value={fmtDateTime(doc.booking_modified)} />
        <MetaRow label="Buchung offen" value={fmtBool(doc.booking_is_open)} />
        <MetaRow label="Hero-Dokument-ID" value={String(doc.id ?? "—")} />
        <MetaRow label="Kunden-Dokument-ID" value={String(doc.customer_document_id ?? "—")} />
        <MetaRow label="Nummer" value={String(doc.nr || "—")} />
      </dl>

      <footer className="eh-project-doc-card__footer flex flex-wrap gap-3">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
          >
            {isDraft ? "In Hero bearbeiten" : "Dokument öffnen"}
          </a>
        ) : null}
        {canBillingReopen && onBillingReopen ? (
          <button
            type="button"
            onClick={onBillingReopen}
            className="text-sm font-medium text-accent hover:underline"
          >
            Als Teil-/Vorkasse markieren
          </button>
        ) : null}
      </footer>
    </article>
  );
}

export function countProjectDocuments(docs: HeroDocument[]): number {
  return docs.length;
}

export function ProjectDocumentsTab({
  orderId,
  heroProjectId,
}: {
  orderId: number;
  heroProjectId?: number | null;
}) {
  const [docs, setDocs] = useState<HeroDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingSubject, setBillingSubject] = useState<BillingReopenSubject | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const list = await getHeroDocuments(orderId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (err) {
      if (!options?.silent) setDocs([]);
      setError(err instanceof Error ? err.message : "Dokumente konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Realtime-Änderungen still einpflegen – der Tab bleibt dabei sichtbar.
  useRealtime({
    scopes: [`auftraege:${orderId}`],
    domains: ["hero", "auftraege", "projects", "dokumente"],
    events: [
      "hero_changed",
      "auftraege_changed",
      "projects_changed",
      "dokument_status_changed",
    ],
    enabled: !!orderId,
    onChange: () => {
      void load({ silent: true });
    },
  });

  const sorted = useMemo(() => {
    if (!docs) return [];
    return [...docs].sort((a, b) => documentSortKey(b) - documentSortKey(a));
  }, [docs]);

  const stats = useMemo(() => {
    const drafts = sorted.filter((d) => d.is_draft).length;
    const withValue = sorted.filter((d) => d.value != null && Number.isFinite(d.value));
    const totalValue = withValue.reduce((sum, d) => sum + Number(d.value), 0);
    const openBookings = sorted.filter((d) => d.booking_is_open === true).length;
    return {
      total: sorted.length,
      drafts,
      totalValue,
      openBookings,
    };
  }, [sorted]);

  if (loading && docs === null) {
    return (
      <div className="space-y-4">
        <ProjectAppDocumentsSection auftragId={orderId} />
        <div className="flex items-center justify-center py-16">
          <Spinner />
          <span className="ml-2 text-sm text-label-secondary">Lade Hero-Dokumente …</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProjectAppDocumentsSection auftragId={orderId} />

      {!heroProjectId ? (
        <div className="rounded-ios-lg border border-separator bg-card px-4 py-10 text-center">
          <p className="text-sm text-label-secondary">
            Für dieses Projekt ist kein Hero-Projekt verknüpft — es können keine Hero-Dokumente
            angezeigt werden.
          </p>
        </div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : (
        <>
          <div>
            <h3 className="mb-3 text-base font-semibold text-label">Hero-Dokumente</h3>
            <div className="eh-project-doc-stats grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
                <p className="text-xs text-label-tertiary">Dokumente</p>
                <p className="text-lg font-semibold tabular-nums text-label">{stats.total}</p>
              </div>
              <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
                <p className="text-xs text-label-tertiary">Entwürfe</p>
                <p className="text-lg font-semibold tabular-nums text-label">{stats.drafts}</p>
              </div>
              <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
                <p className="text-xs text-label-tertiary">Summe Beträge</p>
                <p className="text-lg font-semibold tabular-nums text-label">{fmtMoney(stats.totalValue)}</p>
              </div>
              <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
                <p className="text-xs text-label-tertiary">Offene Buchungen</p>
                <p className="text-lg font-semibold tabular-nums text-label">{stats.openBookings}</p>
              </div>
            </div>
          </div>

          {!sorted.length ? (
            <div className="rounded-ios-lg border border-separator bg-card px-4 py-10 text-center">
              <p className="text-sm text-label-secondary">
                Keine Angebote oder Rechnungen für dieses Projekt in Hero gefunden.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((doc, idx) => {
                const heroId = doc.id != null ? Number(doc.id) : NaN;
                const canReopen =
                  Number.isFinite(heroId) && isHeroInvoice(doc) && doc.is_draft !== true;
                return (
                  <DocumentCard
                    key={String(doc.id ?? doc.nr ?? idx)}
                    doc={doc}
                    canBillingReopen={canReopen}
                    onBillingReopen={() => {
                      setBillingSubject({
                        source: "hero",
                        id: heroId,
                        label: String(doc.nr || documentTitle(doc) || "Hero-Rechnung"),
                        auftragId: orderId,
                        currentArt:
                          doc.billing_art_override || doc.effective_rechnung_art || null,
                      });
                    }}
                  />
                );
              })}
            </div>
          )}

          <p className={clsx("text-xs text-label-tertiary")}>
            Angezeigt werden Hero-Angebote und -Rechnungen zum verknüpften Projekt. Die Markierung
            als Teil-/Vorkasse gilt in der App (Workflow/Fertigstellung) und wird nicht nach Hero
            zurückgeschrieben.
          </p>
        </>
      )}

      <BillingReopenDialog
        open={billingSubject != null}
        subject={billingSubject}
        onClose={() => setBillingSubject(null)}
        onDone={() => {
          setBillingSubject(null);
          void load({ silent: true });
        }}
      />
    </div>
  );
}
